import {
  BaseBulkCRUDAlertIndicatorConfigurations,
  MetaDataKeys,
} from 'src/services/wallet';
import {
  AlertConfiguration,
  IndicatorAlertCriticalityEnum,
  IndicatorAlertType,
} from 'src/types/indicator-alert-configuration';
import { v4 as uuidv4 } from 'uuid';

// Função para gerar ID interno único
export const generateInternalId = (): string => {
  return uuidv4();
};

export function prepareAlertsToPersist(
  walletId: string,
  fundId: string,
  data: {
    percentageAlerts: AlertConfiguration[];
    amountAlerts: AlertConfiguration[];
    alertsIdsToDelete: number[];
  },
): {
  toCreate: AlertConfiguration[];
  toUpdate: AlertConfiguration[];
  toDelete: AlertConfiguration[];
  metadata: {
    walletId: string;
    fundId: string;
  };
} {
  const { percentageAlerts, amountAlerts, alertsIdsToDelete } = data;
  const toCreate: AlertConfiguration[] = [];
  const toUpdate: AlertConfiguration[] = [];

  percentageAlerts.forEach((alert) => {
    if (alert.id === null) {
      toCreate.push(alert);
    } else {
      toUpdate.push(alert);
    }
  });

  amountAlerts.forEach((alert) => {
    if (alert.id === null) {
      toCreate.push(alert);
    } else {
      toUpdate.push(alert);
    }
  });

  const toDelete: AlertConfiguration[] = [];
  alertsIdsToDelete.forEach((id) => {
    const alert = createDefaultAlert(Number(fundId), '', 'percentage');
    alert.id = id;
    alert.indicator = 'dc';
    toDelete.push(alert);
  });

  const toPersist = {
    toCreate,
    toUpdate,
    toDelete,
    metadata: {
      walletId,
      fundId,
    },
  };

  return toPersist;
}

export function prepareDefaultAlertsToPersist(
  entityId: number,
  data: {
    percentageAlerts: AlertConfiguration[];
    amountAlerts: AlertConfiguration[];
    alertsIdsToDelete: number[];
  },
  entityType: 'owner' | 'wallet' = 'owner',
): BaseBulkCRUDAlertIndicatorConfigurations<{
  [key in MetaDataKeys]?: string | number;
}> {
  const { percentageAlerts, amountAlerts, alertsIdsToDelete } = data;
  const toCreate: AlertConfiguration[] = [];
  const toUpdate: AlertConfiguration[] = [];

  percentageAlerts.forEach((alert) => {
    if (alert.id === null) {
      toCreate.push({
        ...alert,
        type:
          entityType === 'owner'
            ? IndicatorAlertType.DEFAULT
            : IndicatorAlertType.WALLET,
      });
    } else {
      toUpdate.push({
        ...alert,
        type:
          entityType === 'owner'
            ? IndicatorAlertType.DEFAULT
            : IndicatorAlertType.WALLET,
      });
    }
  });

  amountAlerts.forEach((alert) => {
    if (alert.id === null) {
      toCreate.push(alert);
    } else {
      toUpdate.push(alert);
    }
  });

  const toDelete: AlertConfiguration[] = [];
  alertsIdsToDelete.forEach((id) => {
    const alert = createDefaultAlert(
      Number(entityId),
      '',
      'percentage',
      entityType === 'owner'
        ? IndicatorAlertType.DEFAULT
        : IndicatorAlertType.WALLET,
    );
    alert.id = id;
    alert.indicator = 'dc';
    toDelete.push(alert);
  });

  const toPersist = {
    toCreate,
    toUpdate,
    toDelete,
    metadata: {
      [entityType === 'owner' ? 'ownerId' : 'walletId']: entityId,
    },
  };

  return toPersist;
}

// Função para criar um alerta padrão
export function createDefaultAlert(
  entityId: number,
  indicator: string,
  valueType: 'percentage' | 'amount',
  type: IndicatorAlertType = IndicatorAlertType.FUND,
): AlertConfiguration {
  return {
    id: null,
    internalId: generateInternalId(),
    type,
    entityId,
    indicator,
    startValue: 0,
    endValue: undefined,
    operator: 'gt',
    criticality: IndicatorAlertCriticalityEnum.ALERT,
    isActive: false,
    email: false,
    alertConfiguration: undefined,
    tabType: valueType,
  };
}

// Função para mapear dados do backend para AlertConfiguration
export function mapToAlertConfiguration(
  alert: any, // Estrutura real do backend
  valueType: 'percentage' | 'amount',
): AlertConfiguration {
  return {
    id: alert.id,
    internalId: generateInternalId(),
    type: alert.type,
    entityId: alert.entityId,
    indicator: alert.indicator.column, // accessorKey
    startValue: alert.criteriaStartValue,
    endValue: alert.criteriaEndValue,
    operator: alert.criteriaOperator,
    criticality: alert.criticality,
    isActive: alert.isActive,
    email: alert.email || false,
    alertConfiguration: alert.alertConfigurationId,
    tabType: alert.indicator.valueType || valueType,
  };
}

// Função para verificar se um alerta está vazio (padrão)
export const isEmptyAlert = (alert: AlertConfiguration): boolean => {
  return (
    alert.startValue === 0 &&
    (alert.endValue === undefined || alert.endValue === 0) &&
    alert.operator === 'gt' &&
    alert.criticality === IndicatorAlertCriticalityEnum.ALERT &&
    alert.isActive === false &&
    alert.email === false
  );
};

// Função para verificar se um alerta foi modificado
export const hasAlertChanged = (
  current: AlertConfiguration,
  original: any,
): boolean => {
  // Comparar campos corretos do backend
  const startValueChanged = current.startValue !== original.criteriaStartValue;
  const endValueChanged = current.endValue !== original.criteriaEndValue;
  const operatorChanged = current.operator !== original.criteriaOperator;
  const criticalityChanged = current.criticality !== original.criticality;
  const isActiveChanged = current.isActive !== original.isActive;
  const emailChanged = current.email !== (original.email || false);

  // Retornar true se QUALQUER campo foi modificado
  return (
    startValueChanged ||
    endValueChanged ||
    operatorChanged ||
    criticalityChanged ||
    isActiveChanged ||
    emailChanged
  );
};

// Função para preparar dados para a API
export function prepareDataForAPI(
  toCreate: AlertConfiguration[],
  toUpdate: AlertConfiguration[],
  toDelete: AlertConfiguration[],
  walletId: string,
  fundId: string,
) {
  return {
    toCreate: toCreate.map((alert) => ({
      id: null,
      indicator: alert.indicator,
      operator: alert.operator,
      startValue: alert.startValue,
      endValue: alert.endValue,
      isActive: alert.isActive,
      email: alert.email,
    })),
    toUpdate: toUpdate.map((alert) => ({
      id: alert.id as number,
      indicator: alert.indicator,
      operator: alert.operator,
      startValue: alert.startValue,
      endValue: alert.endValue,
      isActive: alert.isActive,
      email: alert.email,
    })),
    toDelete: toDelete.map((alert) => ({
      id: alert.id as number,
      indicator: alert.indicator,
      operator: alert.operator,
      startValue: alert.startValue,
      endValue: alert.endValue,
      isActive: alert.isActive,
      email: alert.email,
    })),
    metadata: {
      walletId,
      fundId,
    },
  };
}
